import React, { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { apiClient } from '../utils/apiClient';
import { createCroppedImage, blobToFile } from '../utils/imageCrop';

interface ProfileImageUploadProps {
  currentImageUrl?: string | null;
  onImageUpdate: (imageUrl: string | null) => void;
  className?: string;
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  currentImageUrl,
  onImageUpdate,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
  const MAX_SIZE_MB = 5;

  const validateImage = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Please upload a JPEG, PNG, or WebP image';
    }

    if (file.size > MAX_SIZE_BYTES) {
      return `Image size must be less than ${MAX_SIZE_MB}MB`;
    }

    return null;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validationError = validateImage(file);
    if (validationError) {
      setError(validationError);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setError(null);

    // Read file and show cropper
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setOriginalFile(file);
      setShowCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      // Set default crop area (will be updated by cropper)
      setCroppedAreaPixels({
        x: 0,
        y: 0,
        width: 300,
        height: 300
      });
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = async () => {
    if (!imageToCrop || !croppedAreaPixels || !originalFile) return;

    try {
      setIsUploading(true);
      setError(null);

      // Create cropped image blob
      const croppedBlob = await createCroppedImage(imageToCrop, croppedAreaPixels);

      // Convert blob to file
      const croppedFile = blobToFile(croppedBlob, originalFile.name);

      // Close cropper
      setShowCropper(false);
      setImageToCrop(null);
      setOriginalFile(null);

      // Upload cropped image
      await handleImageUpload(croppedFile);
    } catch (err) {
      console.error('Crop failed:', err);
      setError('Failed to crop image. Please try again.');
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setOriginalFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      // Step 1: Get presigned URL from backend
      const uploadUrlResponse = await apiClient.get('/v1/profile/image/upload-url', {
        params: { contentType: file.type }
      });

      const { upload_url, s3_key } = uploadUrlResponse.data.data;

      // Step 2: Upload directly to S3 using presigned URL
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      // Step 3: Confirm upload to backend
      const confirmResponse = await apiClient.post('/v1/profile/image', { s3_key });

      const newImageUrl = confirmResponse.data.data.profile_image_url;
      setPreviewUrl(newImageUrl);
      onImageUpdate(newImageUrl);

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      let errorMessage = 'Failed to upload image. Please try again.';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as any;
        if (axiosError.response?.data?.error?.message) {
          errorMessage = axiosError.response.data.error.message;
        }
      }

      setError(errorMessage);

      // Clear file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirm(true);
  };

  const handleRemoveConfirm = async () => {
    setIsRemoving(true);
    setError(null);
    setShowRemoveConfirm(false);

    try {
      await apiClient.delete('/v1/profile/image');
      setPreviewUrl(null);
      onImageUpdate(null);
    } catch (err) {
      console.error('Image removal failed:', err);
      let errorMessage = 'Failed to remove image. Please try again.';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as any;
        if (axiosError.response?.data?.error?.message) {
          errorMessage = axiosError.response.data.error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveCancel = () => {
    setShowRemoveConfirm(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-crop me-2"></i>
                  Crop Profile Image
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCropCancel}
                  disabled={isUploading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '400px',
                      backgroundColor: '#333'
                    }}
                  >
                    <Cropper
                      image={imageToCrop}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="zoom-slider" className="form-label">
                    <i className="fas fa-search-plus me-2"></i>
                    Zoom
                  </label>
                  <input
                    id="zoom-slider"
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="form-range"
                  />
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">1x</small>
                    <small className="text-muted">3x</small>
                  </div>
                </div>

                <div className="alert alert-info mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Tip:</strong> Drag to reposition, use the slider to zoom, or scroll with your mouse wheel
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCropCancel}
                  disabled={isUploading}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCropConfirm}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-2"></i>
                      Save & Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`profile-image-upload ${className}`}>
        <div className="mb-3">
          <label className="form-label fw-bold">Profile Image</label>
          <div className="d-flex align-items-start gap-3">
          {/* Image Preview */}
          <div className="profile-image-preview">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Profile"
                className="rounded-circle"
                style={{
                  width: '120px',
                  height: '120px',
                  objectFit: 'cover',
                  border: '3px solid #dee2e6'
                }}
              />
            ) : (
              <div
                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                style={{
                  width: '120px',
                  height: '120px',
                  border: '3px solid #dee2e6'
                }}
              >
                <i className="fas fa-user fa-3x text-muted"></i>
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-grow-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={isUploading || isRemoving}
              data-testid="profile-image-input"
            />

            <div className="d-flex gap-2 mb-2">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleUploadClick}
                disabled={isUploading || isRemoving}
              >
                {isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload me-2"></i>
                    {previewUrl ? 'Change Image' : 'Upload Image'}
                  </>
                )}
              </button>

              {previewUrl && !showRemoveConfirm && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleRemoveClick}
                  disabled={isUploading || isRemoving}
                >
                  {isRemoving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Removing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Remove
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Remove Confirmation */}
            {showRemoveConfirm && (
              <div className="alert alert-warning mb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <span>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Remove profile image?
                  </span>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleRemoveConfirm}
                      disabled={isRemoving}
                    >
                      Yes, Remove
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleRemoveCancel}
                      disabled={isRemoving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <small className="text-muted d-block">
              Max {MAX_SIZE_MB}MB • JPEG, PNG, or WebP format
            </small>

            {error && (
              <div className="alert alert-danger mt-2 mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProfileImageUpload;
